import { FormattedMessage, useIntl } from 'react-intl'

import styled from 'styled-components'

import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { FormattedDateTime } from '@10xLabs/react-web/components/FormattedDateTime'
import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { DrawerBookings } from 'features/SitesSales/components/DrawerBookings'
import { DrawerCancelledTickets } from 'features/SitesSales/components/DrawerCancelledTickets'
import { DrawerSales } from 'features/SitesSales/components/DrawerSales'
import { DrawerStatus } from 'features/SitesSales/components/DrawerStatus'
import { isSuperAdmin, canReadDrawerBookings, canReadDrawerSales, canReadDrawerKreditSales } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { sanitizePrice } from 'utils/math'
import { DrawerActions } from './Actions'
import { ReactElement } from 'react'

const DrawerStatusContainer = styled.div`
  margin-top: -10px;
`

const DifferenceAmountIndicator = styled.div<TSFixMe>`
  color: ${({ difference }): TSFixMe => (difference >= 0 ? '#4caf50' : '#d84315')};
`

export const DrawersList = ({ drawers = [] }): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  const showCompany = isSuperAdmin(login.accessToken.conditions)
  const canShowDrawerBookings = canReadDrawerBookings(login.accessToken.policies)
  const canShowDrawerSales = canReadDrawerSales(login.accessToken.policies)
  const canShowDrawerKreditSales = canReadDrawerKreditSales(login.accessToken.policies)

  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            <Th />
            <Th>
              <FormattedMessage id="drawers.list.startTime" defaultMessage="Start Time" />
            </Th>
            <Th>
              <FormattedMessage id="drawers.list.endTime" defaultMessage="End Time" />
            </Th>
            <Th>
              <FormattedMessage id="drawers.list.salesAgent" defaultMessage="Sales Agent" />
            </Th>
            <Th>
              <FormattedMessage id="drawers.list.bookings" defaultMessage="Bookings" />
            </Th>
            {canShowDrawerBookings && (
              <Th>
                <FormattedMessage id="drawers.list.cancelledTickets" defaultMessage="Cancelled tickets" />
              </Th>
            )}
            {canShowDrawerSales && (
              <Th>
                <FormattedMessage id="drawers.list.items" defaultMessage="Snack sales" />
              </Th>
            )}
            {canShowDrawerSales && (
              <Th>
                <FormattedMessage id="drawers.list.mobileItems" defaultMessage="Mobile sales" />
              </Th>
            )}
            {canShowDrawerKreditSales && (
              <Th>
                <FormattedMessage id="drawers.list.kreditItems" defaultMessage="Kredit sales" />
              </Th>
            )}
            <Th>
              <FormattedMessage id="drawers.list.expectedAmount" defaultMessage="Expected Amount" />
            </Th>
            <Th>
              <FormattedMessage id="drawers.list.actualAmount" defaultMessage="Actual Amount" />
            </Th>
            <Th>
              <FormattedMessage id="drawers.list.difference" defaultMessage="Difference" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.status" defaultMessage="Status" />
            </Th>
            {showCompany && (
              <Th>
                <FormattedMessage id="dictionary.company" defaultMessage="Company" />
              </Th>
            )}
          </Tr>
        </Thead>
        <Tbody>
          {drawers.map((drawer: TSFixMe, index): ReactElement => {
            const expectedAmount = sanitizePrice(
              drawer.expectedAmount.bookings +
                drawer.expectedAmount.sales +
                drawer.expectedAmount.mobileSales +
                drawer.expectedAmount.kreditSales
            )
            return (
              <Tr key={index}>
                <Td top>
                  <DrawerActions drawer={drawer} />
                </Td>
                <Td top>
                  <FormattedDateTime date={drawer.startTime} intl={intl} />
                </Td>
                <Td top>{drawer.endTime ? <FormattedDateTime date={drawer.endTime} intl={intl} /> : '-'}</Td>
                <Td top>
                  {drawer.user.firstName} {drawer.user.lastName}
                </Td>
                <Td top>
                  <DrawerBookings bookings={drawer.drawerBookings} expectedAmount={drawer.expectedAmount.bookings} />
                </Td>
                {canShowDrawerBookings && (
                  <Td top>
                    <DrawerCancelledTickets bookings={drawer.drawerBookings} />
                  </Td>
                )}
                {canShowDrawerSales && (
                  <Td top>
                    <DrawerSales
                      sales={drawer.drawerSales}
                      expectedAmount={drawer.expectedAmount.sales}
                      title={<FormattedMessage id="drawer.sales.title" defaultMessage="Snack Sales" />}
                    />
                  </Td>
                )}
                {canShowDrawerSales && (
                  <Td top>
                    <DrawerSales
                      sales={drawer.drawerMobileSales}
                      expectedAmount={drawer.expectedAmount.mobileSales}
                      title={<FormattedMessage id="drawer.mobileSales.title" defaultMessage="Mobile Sales" />}
                    />
                  </Td>
                )}
                {canShowDrawerKreditSales && (
                  <Td top>
                    <DrawerSales
                      sales={drawer.drawerKreditSales}
                      expectedAmount={drawer.expectedAmount.kreditSales}
                      title={<FormattedMessage id="drawer.kreditSales.title" defaultMessage="Credits Sales" />}
                    />
                  </Td>
                )}
                <Td top>
                  <FormattedPrice value={expectedAmount} />
                </Td>
                <Td top>
                  {drawer.actualAmount || drawer.actualAmount === 0 ? (
                    <FormattedPrice value={drawer.actualAmount} />
                  ) : (
                    '-'
                  )}
                </Td>
                <Td top>
                  {drawer.actualAmount || drawer.actualAmount === 0 ? (
                    <DifferenceAmountIndicator difference={drawer.actualAmount - expectedAmount}>
                      <FormattedPrice value={sanitizePrice(drawer.actualAmount - expectedAmount)} />
                    </DifferenceAmountIndicator>
                  ) : (
                    '-'
                  )}
                </Td>
                <Td top>
                  <DrawerStatusContainer>
                    {drawer.endTime ? (
                      <DrawerStatus status={drawer.status}>
                        <FormattedMessage id="drawer.status.closed" defaultMessage="Closed" />
                      </DrawerStatus>
                    ) : (
                      <DrawerStatus status={drawer.status}>
                        <FormattedMessage id="drawer.status.open" defaultMessage="Open" />
                      </DrawerStatus>
                    )}
                  </DrawerStatusContainer>
                </Td>
                {showCompany && <Td top>{drawer.company.name}</Td>}
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
